<template>
  <article class="feature-page">
    <div class="max-w-4xl mx-auto px-4 py-10">
      <h2 class="text-2xl lg:text-3xl font-bold text-center mb-4">
        piceditor.io Restore Old Photo: Repair Scratches & Revive Memories
      </h2>
      <p class="text-xl text-center mb-6">
        Breathe new life into old, scratched, and faded photos with AI-powered restoration.
      </p>

      <div class="landing-page-feature-descriptions">
        <div class="landing-page-feature-description">
          <AppComparisonSlider :first="img1" :second="img1a" :stop-at="50" class="col-span-12 lg:col-span-7" />
          <div class="lg:order-first col-span-12 lg:col-span-5">
            <h4>Restore Family Treasures</h4>
            <p>
              Recover cherished family portraits by removing scratches, stains, and damage. Our AI restores the original
              charm of old photos, preserving precious memories with modern clarity.
            </p>
          </div>
        </div>

        <div class="landing-page-feature-description">
          <img :src="img2" class="col-span-12 lg:col-span-7" />
          <div class="col-span-12 lg:col-span-5">
            <h4>Sharpen Faces & Details</h4>
            <p>
              Enhance blurry faces and restore lost details, making every smile and expression stand out. Perfect for
              bringing clarity to vintage photos and important historical images.
            </p>
          </div>
        </div>

        <div class="landing-page-feature-description">
          <AppComparisonSlider :first="img3" :second="img3a" :stop-at="50" class="col-span-12 lg:col-span-7" />
          <div class="lg:order-first col-span-12 lg:col-span-5">
            <h4>Revive Faded Colors</h4>
            <p>
              Restore vibrant colors to washed-out photos, balancing tones and contrasts to make old images look fresh
              and lively again.
            </p>
          </div>
        </div>
      </div>

      <p class="mb-4">
        Our Restore Old Photo tool intelligently repairs scratches, enhances facial features, and revives colors in aged
        photos. With AI-driven precision, you can easily bring clarity and life back to your most treasured images.
      </p>

      <h3 class="text-2xl font-bold mt-12 mb-6">Why Use Restore Old Photo?</h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8 feature-page__specific">
        <div class="flex flex-col space-y-4 items-center text-center">
          <img src="@/images/landing_pages/clean.png" alt="Scratch Removal" />
          <strong>Scratch & Damage Repair</strong>
          <p>
            Automatically detect and remove scratches, stains, and physical damage from old photos for a clean, polished
            look.
          </p>
        </div>

        <div class="flex flex-col space-y-4 items-center text-center">
          <img src="@/images/landing_pages/face.png" alt="Face Clarity Enhancement" />
          <strong>Face Clarity Enhancement</strong>
          <p>Sharpen faces and recover fine details to ensure every portrait looks crisp, clear, and expressive.</p>
        </div>

        <div class="flex flex-col space-y-4 items-center text-center">
          <img src="@/images/landing_pages/color.png" alt="Faded Color Fix" />
          <strong>Revive Faded Colors</strong>
          <p>Bring back the original colors of old, faded photos by rebalancing tones and enhancing vibrancy.</p>
        </div>
      </div>

      <h3 class="text-2xl font-bold mt-12 mb-6">How It Works</h3>
      <ol class="list-decimal pl-5 mb-6">
        <li><strong>Upload Your Photo:</strong> Select an old, damaged, or faded image from your device.</li>
        <li>
          <strong>AI Restoration:</strong> Our AI will repair scratches, enhance clarity, and revive colors
          automatically.
        </li>
        <li><strong>Preview:</strong> Instantly see the restored photo.</li>
        <li><strong>Download:</strong> Save your fully restored image, ready for printing, archiving, or sharing.</li>
      </ol>

      <p>
        Fully browser-based and optimized for all devices, Restore Old Photo lets you effortlessly bring your memories
        back to life with professional-grade quality.
      </p>
    </div>
  </article>
</template>

<script setup lang="ts">
  import AppComparisonSlider from '&/AppComparisonSlider.vue'

  import img1 from '@/images/landing_pages/restore_old_photo/1.png'
  import img1a from '@/images/landing_pages/restore_old_photo/1_after.png'
  import img2 from '@/images/landing_pages/restore_old_photo/2.png'
  import img3 from '@/images/landing_pages/restore_old_photo/3.png'
  import img3a from '@/images/landing_pages/restore_old_photo/3_after.png'
</script>
